/**
 * Microsoft Teams mirrors in the chat entry points (Messages list, push, ToDo, Cockpit, search).
 *
 * A Teams mirror is a Kjuup group with `source: 'teams'` whose only Kjuup member is the owner who
 * connected Teams. It has no room, so the web opens it in the room-less chat pane (Coversation, in
 * its Teams mode) for both `teamsChatType` 'oneOnOne' and 'group', never in ProjectDetail. It is
 * never My Space, and the backend stores it with `isOneToOne: false` even for a 1:1.
 *
 * Pure helpers (no React, no store), ported from the mobile app's utils/openChat.js.
 */

import {
  TEAMS_ID_PREFIX,
  TEAMS_SOURCE,
  TEAMS_UNSUPPORTED_ACTIONS,
  hasTeamsMark,
  isTeamsPayload,
  isTeamsUserId,
  teamsInitials as coreTeamsInitials,
} from "./teamsCore";

export { TEAMS_ID_PREFIX, TEAMS_SOURCE, TEAMS_UNSUPPORTED_ACTIONS, isTeamsUserId };

/**
 * window event: a Teams chat's unread count changed (socket 'unreadChatCount' with source 'teams',
 * relayed by App.js). detail: { group, unreadCount } — an absolute count for that Messages row.
 */
export const TEAMS_UNREAD_EVENT = "kjuup:teamsUnread";

/**
 * window event: a Teams chat was filed under a project room or the room was removed (chat info /
 * Messages row menu). detail: { groupId, project } — project { id, name, themeColor } or null.
 */
export const TEAMS_PROJECT_EVENT = "kjuup:teamsProject";

/**
 * The project room a Teams chat is filed under (`teamsProject` on a list row, `teams.project` on
 * group info), as { id, name, themeColor }, else null. Only the daily report and task context use
 * it: the chat itself stays room-less.
 */
export function teamsProjectOf(x) {
  if (!x || typeof x !== "object") return null;
  const candidates = [
    x.teamsProject,
    x.group && x.group.teamsProject,
    x.teams && x.teams.project,
    x.group && x.group.teams && x.group.teams.project,
    x.groupInfo && x.groupInfo.teamsProject,
    x.groupData && x.groupData.teamsProject,
  ];
  const hit = candidates.find((p) => p && typeof p === "object" && p.id != null);
  return hit ? { id: hit.id, name: hit.name || "", themeColor: hit.themeColor || null } : null;
}

/**
 * True for a Teams mirror: a list row (`row.isTeams` / `row.source` / `row.group.*`), a latestMsg or
 * push payload (`chatSource: 'teams'`), a search hit (`groupData`) or group info.
 */
export function isTeamsChat(x) {
  if (!x || typeof x !== "object") return false;
  return isTeamsPayload(x) || hasTeamsMark(x.groupData) || hasTeamsMark(x.groupInfo);
}

// The chat type the payload states itself (null when it says nothing, as a push does).
function statedTeamsChatType(x) {
  if (!x || typeof x !== "object") return null;
  const raw = x.teamsChatType
    || (x.group && x.group.teamsChatType)
    || (x.groupData && x.groupData.teamsChatType)
    || (x.groupInfo && x.groupInfo.teamsChatType)
    || (x.teams && x.teams.chatType)
    || (x.group && x.group.teams && x.group.teams.chatType)
    || null;
  if (!raw) return null;
  return raw === "oneOnOne" ? "oneOnOne" : "group";
}

/** 'oneOnOne' | 'group' for a Teams mirror ('meeting' counts as 'group'), else null. */
export function teamsChatTypeOf(x) {
  if (!isTeamsChat(x)) return null;
  return statedTeamsChatType(x) || "group";
}

/** A Teams 1:1 (listed like a direct chat, still a group on the server). */
export const isTeamsOneOnOne = (x) => teamsChatTypeOf(x) === "oneOnOne";

/** The chat's unsupported actions: its own list (row.group.unsupportedActions), else the full v1 list. */
export function teamsUnsupportedActions(x) {
  if (!isTeamsChat(x)) return [];
  const own = (x.group && x.group.unsupportedActions)
    || x.unsupportedActions
    || (x.groupInfo && x.groupInfo.unsupportedActions)
    || null;
  return Array.isArray(own) ? own : TEAMS_UNSUPPORTED_ACTIONS;
}

/** True when `action` must be hidden for this chat (always false for a native chat). */
export const hidesChatAction = (x, action) => teamsUnsupportedActions(x).includes(action);

/** Initials for a name ("Anna Berg" → "AB"), for Teams people who have no photo ('' when blank). */
export const teamsInitials = (name) => coreTeamsInitials(name, "");

/** The row's title: the Teams display name (the other person for a 1:1, else the topic). */
export function teamsChatTitle(x) {
  if (!x) return "";
  const pick = (v) => (typeof v === "string" && v.trim() ? v.trim() : "");
  return pick(x.name) || pick(x.group && x.group.name) || pick(x.groupName) || pick(x.title) || "Microsoft Teams";
}

/** The group id of a row, latestMsg, push or search payload. */
export function teamsGroupIdOf(x) {
  if (!x || typeof x !== "object") return null;
  if (x.group && typeof x.group === "object" && x.group.id != null) return x.group.id;
  if (x.groupId != null) return x.groupId;
  if (x.groupData && x.groupData.groupId != null) return x.groupData.groupId;
  return x.id != null ? x.id : null;
}

// Teams groups the app has seen (Messages list rows, latestMsg). Entry points whose payload has no
// Teams flag (ToDo, Cockpit, search) look the group up here before falling back to the native route.
const knownTeams = new Map();

/** Remembers the Teams mirrors among `rows` (list rows or latestMsg payloads); returns how many. */
export function rememberTeamsChats(rows) {
  const list = Array.isArray(rows) ? rows : rows ? [rows] : [];
  let n = 0;
  for (const r of list) {
    if (!r || !isTeamsChat(r)) continue;
    const id = teamsGroupIdOf(r);
    if (id == null) continue;
    const prev = knownTeams.get(String(id)) || {};
    knownTeams.set(String(id), {
      teamsChatType: teamsChatTypeOf(r),
      name: r.name || (r.group && r.group.name) || prev.name || "",
      canWriteMessages: r.canWriteMessages != null ? r.canWriteMessages : r.group && r.group.canWriteMessages != null ? r.group.canWriteMessages : prev.canWriteMessages,
      webUrl: r.webUrl || (r.group && r.group.webUrl) || prev.webUrl || null,
      unsupportedActions: (r.group && Array.isArray(r.group.unsupportedActions) && r.group.unsupportedActions) || prev.unsupportedActions || null,
    });
    n += 1;
  }
  return n;
}

/** What the app knows about Teams group `id`, or null. */
export const knownTeamsChat = (id) => (id == null ? null : knownTeams.get(String(id)) || null);

/** Test-only: forget every remembered Teams group. */
export const forgetTeamsChats = () => knownTeams.clear();

/** True when `x` is a Teams mirror or its group id is a Teams group the app has seen. */
export const isTeamsTarget = (x, groupId) => isTeamsChat(x) || knownTeamsChat(groupId != null ? groupId : teamsGroupIdOf(x)) != null;

/**
 * The open-chat entry (LatestMessages `openChats` / RightBar chat) for a Teams mirror: the room-less
 * chat pane in its Teams mode. `target`: a list row, a payload, or `{ id, name, teamsChatType, ... }`;
 * `extra` is merged last (key, isOpened, unreadCountAtOpen, ...).
 */
export function teamsChatEntry(target, extra = {}) {
  const t = target || {};
  const id = extra.id != null ? extra.id : teamsGroupIdOf(t);
  const known = knownTeamsChat(id) || {};
  const flagged = isTeamsChat(t);
  const canWrite = t.canWriteMessages != null ? t.canWriteMessages
    : t.group && t.group.canWriteMessages != null ? t.group.canWriteMessages
      : known.canWriteMessages;
  const name = (flagged ? teamsChatTitle(t) : "") || known.name || teamsChatTitle(t);
  const g = t.group && typeof t.group === "object" ? t.group : {};
  return {
    ...g,
    id,
    name,
    avatar: null,
    isOneToOne: false,
    isSelfChat: false,
    building: null,
    buildingList: null,
    defaultChatGroup: false,
    // Unknown → writable; the chat pane gets the authoritative flag from group info.
    canWriteMessages: canWrite !== false,
    isTeams: true,
    source: TEAMS_SOURCE,
    teamsChatType: statedTeamsChatType(t) || known.teamsChatType || "group",
    webUrl: t.webUrl || g.webUrl || known.webUrl || null,
    unsupportedActions: flagged ? teamsUnsupportedActions(t) : (known.unsupportedActions || TEAMS_UNSUPPORTED_ACTIONS),
    teams: g.teams || t.teams || null,
    teamsProject: teamsProjectOf(t),
    ...extra,
  };
}

/**
 * Unread count for a Teams `latestMsg`: the server sends the owner's real count (0 for the owner's
 * own messages, from Kjuup or from the Teams client), so the row takes it instead of adding 1.
 * Returns null for native events (they keep the +1 rule) or when the count is missing.
 */
export function teamsLatestUnseen(event, userId) {
  if (!event || !isTeamsChat(event) || !Array.isArray(event.unseenChatCount)) return null;
  if (userId == null) return null;
  const mine = event.unseenChatCount.find((e) => e && e.userId != null && String(e.userId) === String(userId));
  const n = mine ? Number(mine.unseenChatCount) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * A `latestMsg` for a Teams row lacks the list-only fields (the 1:1 display name, write flag,
 * unsupportedActions, Teams state, synthetic members, project room). Keeps them from the existing row so the
 * row keeps its look and hidden actions. Native rows pass through unchanged.
 */
export function mergeTeamsLatestMsg(existing, updated) {
  if (!existing || !updated || !(isTeamsChat(updated) || isTeamsChat(existing))) return updated;
  const eg = existing.group || {};
  const ug = updated.group || {};
  const keep = (a, b) => (a !== undefined && a !== null ? a : b);
  return {
    ...updated,
    source: TEAMS_SOURCE,
    isTeams: true,
    // latestMsg.name is the stored group name, not the 1:1 display name.
    name: keep(existing.name, updated.name),
    teamsChatType: keep(updated.teamsChatType, keep(existing.teamsChatType, ug.teamsChatType)),
    webUrl: keep(existing.webUrl, updated.webUrl),
    canWriteMessages: keep(existing.canWriteMessages, updated.canWriteMessages),
    teamsProject: updated.teamsProject !== undefined ? updated.teamsProject : existing.teamsProject,
    isInternal: false,
    translation: false,
    group: {
      ...ug,
      source: TEAMS_SOURCE,
      isTeams: true,
      name: keep(eg.name, ug.name),
      teamsChatType: keep(ug.teamsChatType, eg.teamsChatType),
      canWriteMessages: keep(eg.canWriteMessages, ug.canWriteMessages),
      unsupportedActions: keep(eg.unsupportedActions, ug.unsupportedActions),
      teams: keep(eg.teams, ug.teams),
      webUrl: keep(eg.webUrl, ug.webUrl),
      users: keep(eg.users, ug.users),
    },
  };
}

/**
 * Messages list refresh for `teamsChatsChanged` without losing the loaded pages or the scroll
 * position: the fresh first page replaces its rows (by group id) and adds new ones; every older row
 * that is already loaded stays. The caller sorts the result as it sorts any page. Rows without a
 * group id (never in the list) are dropped from the fresh page.
 */
export function mergeFirstPageRows(current, firstPage) {
  const keyOf = (r) => (r && r.group && r.group.id != null ? String(r.group.id) : null);
  const fresh = (Array.isArray(firstPage) ? firstPage : []).filter((r) => keyOf(r) != null);
  const freshKeys = new Set(fresh.map(keyOf));
  const kept = (Array.isArray(current) ? current : []).filter((r) => {
    const k = keyOf(r);
    return k != null && !freshKeys.has(k);
  });
  return [...fresh, ...kept];
}

/**
 * Messages list row for a Teams chat the user just started from Kjuup (new-chat picker). The
 * server lists a chat only once it has a message, so this placeholder shows the chat at once; the
 * first message (socket) or the next `teamsChatsChanged` refresh replaces it by group id.
 */
export function newTeamsChatRow({ groupId, name, chatType, now = new Date() }) {
  const teamsChatType = chatType === "oneOnOne" ? "oneOnOne" : "group";
  const group = {
    id: String(groupId),
    name: name || "Microsoft Teams",
    isOneToOne: false,
    isSelfChat: false,
    isPinned: false,
    isTeams: true,
    source: TEAMS_SOURCE,
    teamsChatType,
    canWriteMessages: true,
  };
  return {
    id: `teams-new-${groupId}`,
    message: "",
    messageType: "simple",
    createdAt: new Date(now).toISOString(),
    sender: { id: null, firstName: "", lastName: "", title: null, avatar: null, themeColor: null },
    group,
    isTeams: true,
    source: TEAMS_SOURCE,
    teamsChatType,
    unseenChatCount: 0,
    isOpened: true,
    isTeamsPlaceholder: true,
  };
}
